import { usePersistFn } from '@psl/functions/hooks/usePersistFn';
import { isBlurhashValid } from 'blurhash';
import {
  CSSProperties,
  memo,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { BlurhashCanvas } from 'react-blurhash';
import { ImageResizeMode, View } from 'react-native';
import type { OnLoadEvent } from 'react-native-fast-image';
import { PopImageProps, usePopImage } from './PopImage.shared';

export const PopImage = memo(function PopImage({
  uri,
  resizeTo,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  decodeAsync,
  decodeWidth,
  decodeHeight,
  onLoad: onLoadFromProps,
  resizeMode = 'cover',
  blurhashResizeMode = resizeMode,
  blurhashVisibility = 'auto',
  defaultBlurhash,
  lazyLoad = false,
  lazyLoadTransition = false,
  webImageStyle,
  webImageClassName,
  children,
  style,
}: PopImageProps) {
  const [visible, setVisible] = useState(
    !lazyLoad || !IntersectionObserverManager.available,
  );

  const imgRef = useRef<HTMLImageElement>(null);

  useIntersectionObserver(
    imgRef,
    entry => {
      if (entry.isIntersecting && !visible) {
        setVisible(true);
      }
    },
    Boolean(lazyLoad && uri),
  );

  const { imageUrl, hash, showBlur, onLoaded } = usePopImage(
    uri,
    resizeTo,
    onLoadFromProps,
    {
      blurhashVisibility: !visible ? 'always' : blurhashVisibility,
      defaultBlurhash,
    },
  );

  const isValid = useMemo(
    () => showBlur && isBlurhashValid(hash) && hash.length === 36,
    [showBlur, hash],
  );

  const onImageLoad = useCallback(
    (e: SyntheticEvent<HTMLImageElement, Event>) => {
      const event: OnLoadEvent = {
        nativeEvent: {
          width: e.currentTarget.naturalWidth,
          height: e.currentTarget.naturalHeight,
        },
      };
      onLoaded(event);
    },
    [onLoaded],
  );

  const objectStyle = useMemo<CSSProperties>(() => {
    return {
      objectFit: resizeModeToObjectFit(resizeMode),
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    };
  }, [resizeMode]);

  const imageStyle = useMemo<CSSProperties>(() => {
    return {
      ...webImageStyle,
      ...objectStyle,
      opacity: visible ? 1 : 0,
      transition:
        typeof lazyLoadTransition == 'string'
          ? lazyLoadTransition
          : lazyLoadTransition
            ? 'opacity 0.3s ease-in-out'
            : undefined,
    };
  }, [lazyLoadTransition, objectStyle, visible, webImageStyle]);

  return (
    <View
      style={style}
      css={`
        overflow: hidden;
      `}
    >
      {imageUrl ? (
        <img
          ref={imgRef}
          src={visible ? imageUrl || undefined : undefined}
          style={imageStyle}
          className={webImageClassName}
          onLoad={onImageLoad}
        />
      ) : null}
      {showBlur && isValid && (
        <BlurhashCanvas
          hash={hash}
          width={decodeWidth}
          height={decodeHeight}
          style={{
            ...objectStyle,
            objectFit: resizeModeToObjectFit(blurhashResizeMode),
          }}
        />
      )}
      {children}
    </View>
  );
});

const resizeModeToObjectFit = (
  resizeMode: ImageResizeMode,
): CSSProperties['objectFit'] => {
  switch (resizeMode) {
    case 'cover':
      return 'cover';
    case 'stretch':
      return 'fill';
    default:
      return 'contain';
  }
};

type Callback = (entry: IntersectionObserverEntry) => void;

class IntersectionObserverManager {
  private observer: IntersectionObserver | undefined;
  private callbacks = new Map<Element, Callback>();

  static readonly available = 'IntersectionObserver' in window;

  static shared = new IntersectionObserverManager({
    root: null,
    rootMargin: '0px',
    threshold: 0.1,
  });

  constructor(options?: IntersectionObserverInit) {
    if (IntersectionObserverManager.available) {
      this.observer = new IntersectionObserver(this.handleEntries, options);
    }
  }

  private handleEntries = (entries: IntersectionObserverEntry[]) => {
    entries.forEach(entry => {
      this.callbacks.get(entry.target)?.(entry);
    });
  };

  observe(target: Element, callback: Callback) {
    if (this.observer != null) {
      this.callbacks.set(target, callback);
      this.observer.observe(target);
    }
  }

  unobserve(target: Element) {
    if (this.observer != null) {
      this.callbacks.delete(target);
      this.observer.unobserve(target);
    }
  }
}

function useIntersectionObserver(
  ref: React.RefObject<Element>,
  callback: (entry: IntersectionObserverEntry) => void,
  enabled = true,
) {
  const stableCallback = usePersistFn(callback);

  useEffect(() => {
    const el = ref.current;

    if (!el || !enabled) {
      return;
    }

    IntersectionObserverManager.shared.observe(el, stableCallback);

    return () => {
      IntersectionObserverManager.shared.unobserve(el);
    };
  }, [enabled, ref, stableCallback]);
}
